/**
 * Phase 15.5 P1 — Admin Beats Moderation
 *
 * Lists every beat with quick-action toggles for Feature / Hide /
 * Delete. Includes a "Show reported only" filter to surface
 * user-flagged beats.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRequireAdmin } from "@/lib/useRequireAdmin";
import { optimizeImage } from "@/lib/mediaUrls";

const AdminBeatsPage = () => {
  const { authChecked } = useRequireAdmin();
  const [beats, setBeats] = useState([]);
  const [includeHidden, setIncludeHidden] = useState(true);
  const [onlyReported, setOnlyReported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/beats", {
        params: { include_hidden: includeHidden, only_reported: onlyReported },
      });
      setBeats(r.data?.beats || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load beats");
    } finally { setLoading(false); }
  }, [includeHidden, onlyReported]);

  useEffect(() => { if (authChecked) load(); }, [authChecked, load]);
  const setFeatured = async (beat, featured) => {
    try {
      await api.post(`/admin/beats/${beat.beat_id}/feature`, { featured });
      setBeats((bs) => bs.map((b) => b.beat_id === beat.beat_id ? { ...b, featured } : b));
      toast.success(featured ? "Featured!" : "Unfeatured");
    } catch { toast.error("Failed"); }
  };

  const setHidden = async (beat, hidden) => {
    try {
      await api.post(`/admin/beats/${beat.beat_id}/hide`, { hidden });
      setBeats((bs) => bs.map((b) => b.beat_id === beat.beat_id ? { ...b, hidden } : b));
      toast.success(hidden ? "Hidden from public feed" : "Unhidden");
    } catch { toast.error("Failed"); }
  };

  const remove = async (beat) => {
    if (!window.confirm(`Delete "${beat.title}" permanently?`)) return;
    try {
      await api.delete(`/admin/beats/${beat.beat_id}`);
      setBeats((bs) => bs.filter((b) => b.beat_id !== beat.beat_id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0e] p-4 sm:p-6 text-white" data-testid="admin-beats-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black" data-testid="admin-beats-title">🎹 Beats Moderation</h1>
          <p className="text-sm text-gray-400 mt-1">Feature / Hide / Delete and review reported beats.</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-gray-300">
            <Switch checked={includeHidden} onCheckedChange={setIncludeHidden} data-testid="admin-beats-include-hidden" />
            Include hidden
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-300">
            <Switch checked={onlyReported} onCheckedChange={setOnlyReported} data-testid="admin-beats-only-reported" />
            Reported only
          </label>
          <Button onClick={load} size="sm" variant="outline" className="border-white/15 text-white hover:bg-white/10">
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <Card className="p-4 border-rose-400/40 bg-rose-500/5 text-rose-200" data-testid="admin-beats-error">{error}</Card>
      ) : loading ? (
        <Card className="p-4 text-gray-400">Loading…</Card>
      ) : beats.length === 0 ? (
        <Card className="p-8 text-center bg-white/[0.02] border-white/10" data-testid="admin-beats-empty">
          <div className="text-4xl mb-2">🎹</div>
          <p className="text-gray-300">No beats match this filter.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {beats.map((b) => (
            <Card
              key={b.beat_id}
              data-testid={`admin-beat-row-${b.beat_id}`}
              className={
                "p-3 flex items-center gap-3 border " +
                (b.hidden ? "border-rose-400/30 bg-rose-500/[0.04]" : "border-white/10 bg-white/[0.02]")
              }
            >
              {b.cover_url && (
                <img src={optimizeImage(b.cover_url, 'avatar')} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-bold truncate">{b.title}</span>
                  {b.featured && <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-400/40">FEATURED</span>}
                  {b.hidden && <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 border border-rose-400/40">HIDDEN</span>}
                  {b.reports > 0 && <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-200 border border-orange-400/40">⚑ {b.reports} reports</span>}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  by <span className="text-gray-300">{b.user_id}</span> · {b.bpm ? `${b.bpm} BPM` : "—"} · {b.key || "—"} · ${b.price || 0} {b.license_type}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button onClick={() => setFeatured(b, !b.featured)} size="sm"
                        className={b.featured ? "bg-amber-500 text-black font-bold" : "bg-white/5 border border-white/15 text-white"}
                        data-testid={`admin-beat-feature-${b.beat_id}`}>
                  {b.featured ? "★ Unfeature" : "☆ Feature"}
                </Button>
                <Button onClick={() => setHidden(b, !b.hidden)} size="sm"
                        className={b.hidden ? "bg-emerald-500 text-black font-bold" : "bg-white/5 border border-white/15 text-white"}
                        data-testid={`admin-beat-hide-${b.beat_id}`}>
                  {b.hidden ? "Unhide" : "Hide"}
                </Button>
                <Button onClick={() => remove(b)} size="sm" variant="outline"
                        className="border-rose-400/40 text-rose-200 hover:bg-rose-500/10"
                        data-testid={`admin-beat-delete-${b.beat_id}`}>
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBeatsPage;
